/**
 * Compares two strings using natural sort order, which means that
 * numeric substrings are compared as numbers rather than as strings.
 * This is useful for sorting strings containing numbers in a way that
 * humans would expect (e.g., "file2" comes before "file10").
 *
 * The comparison is case-insensitive and ignores leading/trailing whitespace.
 *
 * @param a - The first string to compare.
 * @param b - The second string to compare.
 * @returns A negative number if `a` comes before `b`, a positive number if `a` comes after `b`, or 0 if they are considered equal.
 */
export function naturalCompare(a: string, b: string): number {
  const normalize = (str: string) => str.toLowerCase().trim();
  const aStr = normalize(a);
  const bStr = normalize(b);

  const aChunks = aStr.match(/(\d+|\D+)/g) || [];
  const bChunks = bStr.match(/(\d+|\D+)/g) || [];

  const maxLength = Math.max(aChunks.length, bChunks.length);

  for (let i = 0; i < maxLength; i++) {
    const aChunk = aChunks[i] || '';
    const bChunk = bChunks[i] || '';

    if (/^\d+$/.test(aChunk) && /^\d+$/.test(bChunk)) {
      const numA = parseInt(aChunk, 10);
      const numB = parseInt(bChunk, 10);
      if (numA !== numB) return numA - numB;
      continue;
    }

    if (aChunk !== bChunk) {
      return aChunk.localeCompare(bChunk);
    }
  }

  return 0;
}
